import React, { useState, useEffect } from "react";
import {
  collection,
  query,
  where,
  orderBy as firestoreOrderBy, // avoid naming conflict
  onSnapshot,
  doc,
  updateDoc,
} from "firebase/firestore";

import { db, auth } from "../../../../firebase";
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Typography,
  TableSortLabel,
} from "@mui/material";
import { Link } from "react-router-dom";

const EventsTable = ({ theme }) => {
  const [events, setEvents] = useState([]);
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("createdAt");
  const [editing, setEditing] = useState({
    rowId: null,
    field: null,
    value: "",
  });

  const updateCell = async (rowId, field, value) => {
    try {
      const eventRef = doc(db, "events", rowId);
      await updateDoc(eventRef, { [field]: value });
    } catch (error) {
      console.error("Error updating cell:", error);
    }
  };

  useEffect(() => {
    const currentUser = auth.currentUser;
    if (!currentUser) return;

    const eventsRef = collection(db, "events");
    const q = query(
      eventsRef,
      where("creatorUid", "==", currentUser.uid),
      firestoreOrderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const eventsData = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setEvents(eventsData);
    });

    return () => unsubscribe();
  }, []);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  function descendingComparator(a, b, orderBy) {
    if (a[orderBy] < b[orderBy]) return -1;
    if (a[orderBy] > b[orderBy]) return 1;
    return 0;
  }

  function getComparator(order, orderBy) {
    return order === "desc"
      ? (a, b) => descendingComparator(b, a, orderBy)
      : (a, b) => descendingComparator(a, b, orderBy);
  }

  function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const orderVal = comparator(a[0], b[0]);
      if (orderVal !== 0) return orderVal;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  }

  const sortedEvents = stableSort(events, getComparator(order, orderBy));

  return (
    <TableContainer
      component={Paper}
      sx={{
        marginTop: 4,
        fontFamily: "basic-sans",
        backgroundColor: theme.colors.mediumPurple,
        color: "white",
      }}
    >
      {events.length === 0 ? (
        <Typography variant="body1" sx={{ padding: 2, color: "white" }}>
          No events found.
        </Typography>
      ) : (
        <Table>
          <TableHead
            sx={{
              backgroundColor: theme.colors.headerPurple,
              "& th": {
                color: theme.colors.amber,
                fontFamily: "basic-sans",
                fontWeight: 700,
                borderBottom: `1px solid ${theme.colors.palePurple}`,
              },
            }}
          >
            <TableRow>
              {[
                "title",
                "description",
                "startDate",
                "endDate",
                "price",
                "createdAt",
                "content",
              ].map((col) => (
                <TableCell key={col}>
                  {col === "content" ? (
                    // Non-sortable header
                    <span
                      style={{
                        color: theme.colors.amber,
                        fontFamily: "basic-sans",
                        fontWeight: 700,
                      }}
                    >
                      {col.charAt(0).toUpperCase() + col.slice(1)}
                    </span>
                  ) : (
                    // Sortable header
                    <TableSortLabel
                      active={orderBy === col}
                      direction={orderBy === col ? order : "asc"}
                      onClick={() => handleRequestSort(col)}
                      sx={{
                        color: theme.colors.amber,
                        // Style for active header
                        "&.Mui-active": {
                          color: theme.colors.white,
                        },
                        "& .MuiTableSortLabel-icon": {
                          color: theme.colors.amber,
                        },
                        "&.Mui-active .MuiTableSortLabel-icon": {
                          color: theme.colors.white,
                        },
                        // Hover styles
                        "&:hover": {
                          color: theme.colors.green,
                          "& .MuiTableSortLabel-icon": {
                            color: theme.colors.green,
                          },
                        },
                      }}
                    >
                      {col.charAt(0).toUpperCase() + col.slice(1)}
                    </TableSortLabel>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody
            sx={{
              backgroundColor: theme.colors.mediumPurple,
              "& td": {
                color: "white",
                fontFamily: "basic-sans",
                fontWeight: 300,
                borderBottom: `1px solid ${theme.colors.palePurple}`,
              },
            }}
          >
            {sortedEvents.map((event) => (
              <TableRow key={event.id}>
                {/* Editable Title Cell */}
                <TableCell
                  onClick={() => {
                    setEditing({
                      rowId: event.id,
                      field: "title",
                      value: event.title,
                    });
                  }}
                >
                  {editing.rowId === event.id && editing.field === "title" ? (
                    <input
                      value={editing.value}
                      onChange={(e) =>
                        setEditing({ ...editing, value: e.target.value })
                      }
                      onBlur={async () => {
                        await updateCell(
                          editing.rowId,
                          editing.field,
                          editing.value
                        );
                        setEditing({ rowId: null, field: null, value: "" });
                      }}
                      onKeyUp={async (e) => {
                        if (e.key === "Enter") {
                          await updateCell(
                            editing.rowId,
                            editing.field,
                            editing.value
                          );
                          setEditing({ rowId: null, field: null, value: "" });
                        }
                      }}
                      autoFocus
                      style={{
                        width: "100%",
                        padding: "4px",
                        fontFamily: "basic-sans",
                        minWidth: "250px",
                        outline: "none",
                        borderRadius: "5px",
                      }}
                    />
                  ) : (
                    event.title
                  )}
                </TableCell>
                <TableCell
                  onClick={() => {
                    setEditing({
                      rowId: event.id,
                      field: "description",
                      value: event.description,
                    });
                  }}
                >
                  {editing.rowId === event.id &&
                  editing.field === "description" ? (
                    <input
                      value={editing.value}
                      onChange={(e) =>
                        setEditing({ ...editing, value: e.target.value })
                      }
                      onBlur={async () => {
                        await updateCell(
                          editing.rowId,
                          editing.field,
                          editing.value
                        );
                        setEditing({ rowId: null, field: null, value: "" });
                      }}
                      onKeyUp={async (e) => {
                        if (e.key === "Enter") {
                          await updateCell(
                            editing.rowId,
                            editing.field,
                            editing.value
                          );
                          setEditing({ rowId: null, field: null, value: "" });
                        }
                      }}
                      autoFocus
                      style={{
                        width: "100%",
                        padding: "4px",
                        fontFamily: "basic-sans",
                        minWidth: "250px",
                        outline: "none",
                        borderRadius: "5px",
                      }}
                    />
                  ) : (
                    event.description
                  )}
                </TableCell>
                <TableCell
                  onClick={() => {
                    setEditing({
                      rowId: event.id,
                      field: "startDate",
                      // Format date as YYYY-MM-DD for the date input's value
                      value: event.startDate?.toDate
                        ? event.startDate.toDate().toISOString().split("T")[0]
                        : new Date(event.startDate).toISOString().split("T")[0],
                    });
                  }}
                >
                  {editing.rowId === event.id &&
                  editing.field === "startDate" ? (
                    <input
                      type="date"
                      value={editing.value}
                      onChange={(e) =>
                        setEditing({ ...editing, value: e.target.value })
                      }
                      onBlur={async () => {
                        // Find the current event data for validation
                        const currentEvent = events.find(
                          (e) => e.id === editing.rowId
                        );
                        // Parse the new start date
                        const newStart = new Date(editing.value);
                        // Get the current end date of the event
                        const currentEnd = currentEvent?.endDate?.toDate
                          ? currentEvent.endDate.toDate()
                          : new Date(currentEvent.endDate);

                        // Validate that new start date is before end date
                        if (newStart >= currentEnd) {
                          alert("Start date must be before the end date.");
                          return;
                        }

                        // If validation passes, update the cell in Firestore
                        await updateCell(
                          editing.rowId,
                          editing.field,
                          newStart
                        );
                        // Reset editing state
                        setEditing({ rowId: null, field: null, value: "" });
                      }}
                      autoFocus
                      style={{
                        width: "100%",
                        padding: "4px",
                        fontFamily: "basic-sans",
                        outline: "none",
                        borderRadius: "5px",
                      }}
                    />
                  ) : event.startDate?.toDate ? (
                    event.startDate.toDate().toLocaleDateString()
                  ) : (
                    new Date(event.startDate).toLocaleDateString()
                  )}
                </TableCell>

                <TableCell
                  onClick={() => {
                    setEditing({
                      rowId: event.id,
                      field: "endDate",
                      value: event.endDate?.toDate
                        ? event.endDate.toDate().toISOString().split("T")[0]
                        : new Date(event.endDate).toISOString().split("T")[0],
                    });
                  }}
                >
                  {editing.rowId === event.id && editing.field === "endDate" ? (
                    <input
                      type="date"
                      value={editing.value}
                      onChange={(e) =>
                        setEditing({ ...editing, value: e.target.value })
                      }
                      onBlur={async () => {
                        const currentEvent = events.find(
                          (e) => e.id === editing.rowId
                        );
                        const newEnd = new Date(editing.value);
                        const currentStart = currentEvent?.startDate?.toDate
                          ? currentEvent.startDate.toDate()
                          : new Date(currentEvent.startDate);

                        // Validate that new end date is after start date
                        if (newEnd <= currentStart) {
                          alert("End date must be after the start date.");
                          return;
                        }

                        await updateCell(editing.rowId, editing.field, newEnd);
                        setEditing({ rowId: null, field: null, value: "" });
                      }}
                      autoFocus
                      style={{
                        width: "100%",
                        padding: "4px",
                        fontFamily: "basic-sans",
                        outline: "none",
                        borderRadius: "5px",
                      }}
                    />
                  ) : event.endDate?.toDate ? (
                    event.endDate.toDate().toLocaleDateString()
                  ) : (
                    new Date(event.endDate).toLocaleDateString()
                  )}
                </TableCell>

                <TableCell
                  onClick={() => {
                    setEditing({
                      rowId: event.id,
                      field: "price",
                      value: event.price,
                    });
                  }}
                >
                  {editing.rowId === event.id && editing.field === "price" ? (
                    <input
                      type="number" // Restrict to numeric input
                      inputMode="numeric" // (Optional) Suggests numeric keyboard on mobile
                      min="0" // (Optional) Enforce non-negative values
                      step="any" // (Optional) Allow any numeric input, including decimals
                      value={editing.value}
                      onChange={(e) =>
                        setEditing({ ...editing, value: e.target.value })
                      }
                      onBlur={async () => {
                        // Convert value to a number before updating
                        const numericValue = parseFloat(editing.value);
                        await updateCell(
                          editing.rowId,
                          editing.field,
                          numericValue
                        );
                        setEditing({ rowId: null, field: null, value: "" });
                      }}
                      onKeyUp={async (e) => {
                        if (e.key === "Enter") {
                          await updateCell(
                            editing.rowId,
                            editing.field,
                            editing.value
                          );
                          setEditing({ rowId: null, field: null, value: "" });
                        }
                      }}
                      autoFocus
                      style={{
                        width: "100%",
                        padding: "4px",
                        fontFamily: "basic-sans",
                        outline: "none",
                        borderRadius: "5px",
                      }}
                    />
                  ) : (
                    event.price
                  )}
                </TableCell>

                <TableCell>
                  {event.createdAt?.toDate
                    ? event.createdAt.toDate().toLocaleDateString()
                    : new Date(event.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Link
                    to="/challenge/admin/events/content"
                    state={{ event }} // pass the entire event object
                    style={{
                      color: theme.colors.amber,
                      textDecoration: "none",
                    }}
                  >
                    Edit & Add Content
                  </Link>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </TableContainer>
  );
};

export default EventsTable;
